/**
 * Explore view of the Product Map — the body previously rendered by
 * `ExploreHomePage` before Explore became a `List | Explore` tab on
 * `/product-map` (decision 2026-09-17, see `docs/features/exploration.md`
 * and `docs/features/product-map.md`). Split into:
 *
 * - `useExploreRunNow` — cycles/schedules data + the "Run now" action state,
 *   called once by `ProductMapPage` so the button (which lives in the page
 *   header) and the body (which renders the schedule picker) share one
 *   instance.
 * - `ExploreRunNowAction` — the header's "Run now" button, a thin view over
 *   that state.
 * - `ExploreView` — the map, latest/recent cycles, and open questions.
 *
 * @module features/exploration/components/ExploreView
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Banner,
  Heading,
  StatusBadge,
  Text,
  EmptyState,
  ListCard,
  ListCardRow,
  LoadingOrbit,
  Markdown,
  useToast,
} from '@chatsdet/ui';
import { Compass, ArrowsClockwise, Pause, Play, RocketLaunch, WarningCircle } from '@phosphor-icons/react';
import { useToggleScheduleRule } from '../../scheduling/useScheduleRules';
import { formatDurationFromIso } from '../../../lib/formatDuration';
import { useAssistant } from '../../assistant/AssistantContext';
import { ExplorationMap } from './ExplorationMap';
import { OpenQuestionsCard } from './OpenQuestionsCard';
import {
  useExplorationCycles,
  useExplorationSchedules,
  useExplorationCycle,
  useRunExplorationNow,
  useCancelExplorationCycle,
  isActiveCycleStatus,
  type ExplorationCycleRow,
} from '../useExploration';

/** cycle status -> StatusBadge status. */
function cycleBadgeStatus(status: string): 'success' | 'failed' | 'running' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'failed';
  return 'running';
}

/** A cycle is stale once it's been active well past a normal run's length. */
const STALE_CYCLE_MS = 30 * 60 * 1000;

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function CancelCycleButton({ cycleId }: { cycleId: string }) {
  const cancel = useCancelExplorationCycle();
  const { toast } = useToast();

  const handleCancel = () => {
    if (!window.confirm('Cancel this exploration cycle? Any workers still running will be abandoned.')) {
      return;
    }
    cancel.mutate(cycleId, {
      onSuccess: (result) => {
        toast.success(
          result.workersAbandoned > 0
            ? `Cycle cancelled — ${result.workersAbandoned} worker${result.workersAbandoned === 1 ? '' : 's'} abandoned.`
            : 'Cycle cancelled.'
        );
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to cancel cycle');
      },
    });
  };

  return (
    <Button variant="destructive" size="sm" onPress={handleCancel} isPending={cancel.isPending}>
      Cancel cycle
    </Button>
  );
}

function LatestCycleCard({ cycle }: { cycle: ExplorationCycleRow }) {
  const detailQ = useExplorationCycle(cycle.id);
  const assignments = detailQ.data?.cycle.assignments ?? [];
  const doneCount = assignments.filter((a) => a.status !== 'running').length;
  const isActive = isActiveCycleStatus(cycle.status);
  const isStale = isActive && Date.now() - new Date(cycle.startedAt).getTime() > STALE_CYCLE_MS;

  return (
    <div className="space-y-3">
      {isStale && (
        <Banner variant="warning">
          <div className="flex flex-1 flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <WarningCircle size={16} />
              <Text size="sm">
                This cycle has been running for {formatDurationFromIso(cycle.startedAt)} — longer than usual.
              </Text>
            </div>
            <CancelCycleButton cycleId={cycle.id} />
          </div>
        </Banner>
      )}
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
          <Heading level={3}>{formatDate(cycle.startedAt)}</Heading>
          <StatusBadge status={cycleBadgeStatus(cycle.status)} label={cycle.status} />
        </CardHeader>
        <CardContent className="space-y-3">
          {isActive ? (
            <Text size="xs" className="text-muted-foreground">
              {doneCount} of {cycle.workersPerCycle} explorers done · running {formatDurationFromIso(cycle.startedAt)}
            </Text>
          ) : (
            <Text size="xs" className="text-muted-foreground">
              {cycle.workersPerCycle} workers · took {formatDurationFromIso(cycle.startedAt, cycle.completedAt)}
            </Text>
          )}

          {cycle.failureReason && (
            <Banner variant={cycle.status === 'failed' ? 'error' : 'warning'}>
              <Text size="sm">{cycle.failureReason}</Text>
            </Banner>
          )}

          {cycle.summaryMd ? (
            <div className="prose prose-sm max-w-none">
              <Markdown>{cycle.summaryMd}</Markdown>
            </div>
          ) : isActive ? (
            <Text size="sm" className="text-muted-foreground italic">
              No summary yet.
            </Text>
          ) : cycle.status === 'failed' ? (
            <Text size="sm" className="text-muted-foreground italic">
              Failed before a summary was written.
            </Text>
          ) : null}

          <div className="pt-2">
            <Link
              to="/product-map/cycles/$cycleId"
              params={{ cycleId: cycle.id }}
              className="text-sm text-primary hover:underline"
            >
              View cycle detail →
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Cycles/schedules data + "Run now" state, shared between the header action
 * button and the body's schedule picker. Call once per `ProductMapPage`
 * render (unconditionally, like any other hook) — `enabled` only gates the
 * underlying queries so switching to the List view stops polling.
 */
export function useExploreRunNow(propertyId: string | null, enabled: boolean) {
  const cyclesQ = useExplorationCycles(propertyId ?? undefined, enabled);
  const schedulesQ = useExplorationSchedules(propertyId ?? undefined, enabled);
  const runNow = useRunExplorationNow();
  const toggleSchedule = useToggleScheduleRule();
  const [pickerOpen, setPickerOpen] = useState(false);
  const { startOnboarding } = useAssistant();
  const { toast } = useToast();

  const cycles = cyclesQ.data?.cycles ?? [];
  const schedules = schedulesQ.data?.schedules ?? [];
  const hasActive = cycles.some((c) => isActiveCycleStatus(c.status));
  // A paused schedule starts no cycles, including Run now, until resumed.
  const runnableSchedules = schedules.filter((s) => s.enabled);
  const allPaused = schedules.length > 0 && runnableSchedules.length === 0;

  const runNowDisabledReason =
    schedules.length === 0
      ? 'No exploration schedule for this application'
      : allPaused
        ? 'The exploration schedule is paused'
        : hasActive
          ? 'A cycle is already running'
          : undefined;

  const handleRunNow = () => {
    if (runnableSchedules.length === 0) return;
    if (runnableSchedules.length === 1) {
      runNow.mutate(runnableSchedules[0].ruleId, {
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to start exploration'),
      });
    } else {
      setPickerOpen((v) => !v);
    }
  };

  return {
    cyclesQ,
    cycles,
    schedules,
    runnableSchedules,
    hasActive,
    runNow,
    toggleSchedule,
    pickerOpen,
    setPickerOpen,
    handleRunNow,
    runNowDisabledReason,
    startOnboarding,
  };
}

export type ExploreRunNowState = ReturnType<typeof useExploreRunNow>;

/** The "Run now" button rendered in `ProductMapPage`'s header when the Explore view is active. */
export function ExploreRunNowAction({ state }: { state: ExploreRunNowState }) {
  const { schedules, toggleSchedule } = state;
  const { toast } = useToast();
  // Pause/resume acts on the application's single Explore schedule; with several, each is managed from its own rule.
  const schedule = schedules.length === 1 ? schedules[0] : null;
  return (
    <div className="flex items-center gap-2">
      {schedule && (
        <Button
          variant="ghost"
          size="sm"
          iconLeft={schedule.enabled ? <Pause size={14} /> : <Play size={14} />}
          onPress={() =>
            toggleSchedule.mutate(schedule.ruleId, {
              onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to update schedule'),
            })
          }
          isPending={toggleSchedule.isPending}
        >
          {schedule.enabled ? 'Pause schedule' : 'Resume schedule'}
        </Button>
      )}
      <Button
        variant="primary"
        size="sm"
        iconLeft={<ArrowsClockwise size={14} />}
        onPress={state.handleRunNow}
        isPending={state.runNow.isPending}
        isDisabled={state.runnableSchedules.length === 0 || state.hasActive}
        tooltip={state.runNowDisabledReason}
      >
        Run now
      </Button>
    </div>
  );
}

/** The Explore view's body: map, open questions, latest/recent cycles. Rendered only once `propertyId` is known. */
export function ExploreView({ propertyId, state }: { propertyId: string; state: ExploreRunNowState }) {
  const { cyclesQ, cycles, runNow, pickerOpen, setPickerOpen, startOnboarding } = state;
  const latest = cycles[0] ?? null;
  const { toast } = useToast();

  if (cyclesQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  if (cyclesQ.isError) {
    return (
      <Banner variant="error">
        <Text size="sm">
          {cyclesQ.error instanceof Error ? cyclesQ.error.message : 'Failed to load exploration cycles.'}
        </Text>
      </Banner>
    );
  }

  return (
    <div className="space-y-6">
      <ExplorationMap propertyId={propertyId} />
      <OpenQuestionsCard propertyId={propertyId} />

      {pickerOpen && state.runnableSchedules.length > 1 && (
        <Card>
          <CardContent className="p-3 space-y-2">
            <Text size="sm" weight="semibold">
              Choose schedule to run
            </Text>
            {state.runnableSchedules.map((s) => (
              <div key={s.ruleId} className="flex items-center justify-between gap-2">
                <Text size="sm">{s.name}</Text>
                <Button
                  size="sm"
                  variant="secondary"
                  onPress={() => {
                    runNow.mutate(s.ruleId, {
                      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to start exploration'),
                    });
                    setPickerOpen(false);
                  }}
                >
                  Run
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {latest ? (
        <LatestCycleCard cycle={latest} />
      ) : (
        <EmptyState
          key={propertyId}
          icon={<Compass size={24} />}
          title="No cycles yet"
          description="The assistant will set up exploration for this application — pick personas, confirm logins, and run the first cycle."
          action={
            <Button
              variant="primary"
              iconLeft={<RocketLaunch size={16} weight="fill" />}
              onPress={() => startOnboarding('explore-kickoff', { propertyId })}
            >
              Start exploring
            </Button>
          }
        />
      )}

      {cycles.length > 1 && (
        <ListCard title="Recent cycles">
          {cycles.slice(1).map((c, index) => (
            <ListCardRow
              key={c.id}
              index={index}
              title={
                <Link to="/product-map/cycles/$cycleId" params={{ cycleId: c.id }} className="hover:underline">
                  {formatDate(c.startedAt)}
                </Link>
              }
              badges={<StatusBadge status={cycleBadgeStatus(c.status)} label={c.status} size="sm" />}
              meta={
                <>
                  <Text size="xs">{c.workersPerCycle} workers</Text>
                  {c.failureReason && (
                    <Text
                      size="xs"
                      className={
                        c.status === 'failed'
                          ? 'text-destructive truncate'
                          : 'text-muted-foreground truncate'
                      }
                    >
                      {c.failureReason}
                    </Text>
                  )}
                </>
              }
            />
          ))}
        </ListCard>
      )}
    </div>
  );
}
